<template>
  <section>
    <!-- Exportação de Preços Escalonados -->
    <div class="mb-3">
      <b-card-actions
        :title="$t('Exportar Preço Escalonado')"
      >
        <b-row>
          <b-col md="4">
            <e-store-combo
              v-model="exportFilters.storeId"
              :clearable="true"
              :show-label="true"
              :required="false"
              :only-active-options="true"
              :placeholder="$t('Selecione')"
            />
          </b-col>
          <b-col md="4">
            <FormulateInput
              id="partnership-price-table"
              v-model="exportFilters.priceTableId"
              :type="'vue-select'"
              :label="$t('Tabela de Preço')"
              :options="priceTableOptions"
              :placeholder="$t('Selecione uma tabela de preço')"
            />
          </b-col>
          <b-col md="4">
            <e-search-sku
              id="order-sku_id"
              v-model="exportFilters.skuId"
              :required="false"
              :include-fixed-assets="true"
              :only-active-options="true"
            />
          </b-col>
        </b-row>
        <b-row class="mt-1">
          <b-col
            md="12"
            class="d-flex justify-content-end"
          >
            <slot name="buttons">
              <e-button
                id="download_excel"
                type="button"
                :class="'btn-sm-block mr-1 my-1 my-md-0'"
                variant="primary"
                :busy="downloading"
                icon="download"
                :text="$t('Download')"
                @click="downloadTieredPriceExcel"
              />
            </slot>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="4">
            <small class="text-muted">{{ $t('Filtrar por loja, tabela de preço e/ou SKU para exportar apenas os preços escalonados relacionados aos filtros selecionados.') }}</small>
          </b-col>
        </b-row>
      </b-card-actions>
      <b-card-actions
        :show-loading="uploading"
        action-collapse
        :title="$t('Importar Preço Escalonado')"
      >
        <FormulateForm
          v-show="!uploading"
          ref="form"
        >
          <b-row>
            <b-col md="12">
              <FormulateInput
                id="tiered-price-file"
                ref="tieredPriceFile"
                v-model="localTieredPriceFile"
                type="uploader"
                max-files="1"
                class="required"
                :theme="'list'"
                :deletable="true"
                :meta="true"
                :accept="'.xlsx'"
                :label="$t('Arquivo Excel (.xlsx)')"
                validation="required"
                :help-text="$t('Arraste o arquivo de preços escalonados aqui (ou clique para selecionar o arquivo)')"
                @input="onFileInput"
                @delete="removeFiles"
              />
            </b-col>
          </b-row>
          <b-row class="mt-2">
            <b-col md="12">
              <fab
                :main-tooltip="$t('Importar')"
                main-icon="cloud_upload"
                :busy="uploading"
                :disabled="!hasFile || uploading"
                @click="importFile"
              />
            </b-col>
          </b-row>
        </FormulateForm>
      </b-card-actions>
    </div>
  </section>
</template>

<script>
import { BRow, BCol } from 'bootstrap-vue'
import EStoreCombo from '@/views/components/inputs/EStoreCombo.vue'
import { ESearchSku } from '@/views/components'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import fab from '@/views/components/FAB.vue'
import EButton from '@/views/components/EButton.vue'
import { mapActions, mapGetters } from 'vuex'
import { uploader, alerts } from '@/mixins'

export default {
  name: 'ImportTieredPrice',
  components: {
    BRow,
    BCol,
    BCardActions,
    fab,
    EStoreCombo,
    ESearchSku,
    EButton,
  },
  mixins: [uploader, alerts],
  data() {
    return {
      uploading: false,
      downloading: false,
      tieredPriceFile: [],
      priceTables: [],
      exportFilters: {
        priceTableId: null,
        storeId: null,
        skuId: null,
      },
    }
  },
  computed: {
    ...mapGetters('common/priceTables', ['getComboPriceTables']),
    localTieredPriceFile: {
      get() {
        return this.tieredPriceFile
      },
      set(val) {
        this.tieredPriceFile = val
      },
    },
    hasFile() {
      return this.tieredPriceFile.length > 0
    },

    priceTableOptions() {
      const options = [{ value: null, label: this.$t('Nenhuma tabela selecionada') }]

      this.priceTables.forEach(table => {
        options.push({
          value: table.id,
          label: table.name,
        })
      })

      return options
    },
  },
  async mounted() {
    await this.fetchPriceTables()
  },
  methods: {
    async fetchPriceTables() {
      try {
        const { data } = await this.$http.get('/api/price-tables', {
          params: {
            pageSize: 1000,
            active: true,
          },
        })
        this.priceTables = data.results || []
      } catch (error) {
        this.showGenericError({ error })
      }
    },

    async downloadTieredPriceExcel() {
      try {
        this.downloading = true
        const params = {
          priceTableId: this.exportFilters.priceTableId,
          storeId: this.exportFilters.storeId,
          skuId: this.exportFilters.skuId,
        }
        const blobData = await this.$store.dispatch('pages/catalog/importTieredPrice/exportTieredPriceExcel', params)
        const blob = new Blob([blobData], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = 'precos-escalonados.xlsx'
        link.click()
        window.URL.revokeObjectURL(link.href)
      } catch (error) {
        this.showError({ error, message: error?.response?.data?.message || this.$t('Erro ao exportar arquivo') })
      } finally {
        this.downloading = false
      }

    },
    ...mapActions('pages/catalog/importTieredPrice', ['importTieredPrice']),
    async onFileInput() {
      // Placeholder for any additional logic on file input
    },
    async importFile() {
      if (!this.hasFile) return
      this.uploading = true
      try {
        await this.importTieredPrice({ file: this.tieredPriceFile[0] })
        this.showSuccess({
            message: this.$t('Importação realizada com sucesso!'),
          })
        this.tieredPriceFile = []
      } catch (error) {
        this.showError({ error, message: error?.response?.data?.message || this.$t('Erro ao importar arquivo') })
      } finally {
        this.uploading = false
      }
    },
    removeFiles() {
      this.tieredPriceFile = []
    },
  },
}
</script>
